import React, { useState, useEffect } from 'react';
import { Edit, Trash, X, Plus } from 'lucide-react';
import { fetchConfig, saveConfig, fetchFacilities, addFacility, updateFacility, deleteFacility, fetchExpenseCategoriesList, addExpenseCategory, updateExpenseCategory, deleteExpenseCategory, fetchExpenseCategoriesWithUsage, uploadTermSheetSignature, fetchInvoiceCategories, addInvoiceCategory, updateInvoiceCategory, deleteInvoiceCategory, fetchDepartments, addDepartment, updateDepartment, deleteDepartment, fetchInvoiceRules, addInvoiceRule, updateInvoiceRule, deleteInvoiceRule, fetchInvoiceTypes, addInvoiceType, updateInvoiceType, deleteInvoiceType } from '../utils/api';
import { formatInvoiceNumber } from '../utils/invoiceNumber';

const menuItems = [
  { id: 'company-details', label: 'Company Details' },
  { id: 'invoice-settings', label: 'Invoice Settings' },
  { id: 'quote-settings', label: 'Quote Settings' },
  { id: 'term-sheet-signature', label: 'Term Sheet Signature' },
  { id: 'tax-settings', label: 'Tax Settings' },
  { id: 'bank-accounts', label: 'Bank Accounts' },
  { id: 'invoice-categories', label: 'Invoice Categories' },
  { id: 'departments', label: 'Departments' },
  { id: 'invoice-rules', label: 'Invoice Rules' },
  { id: 'invoice-types', label: 'Invoice Types' },
  { id: 'facilities', label: 'Facilities' },
  { id: 'operational-cost-categories', label: 'Operational Cost Categories' },
];

function Configurations({ facilities: facilitiesProp = [], setFacilities, currentUser }) {
  // Use local state for facilities to ensure they're always available
  const [facilities, setFacilitiesLocal] = useState(facilitiesProp);
  const [config, setConfig] = useState({
    companyName: 'Spatium',
    companyAddress: 'No: 51, Devarabisanahalli, Bangalore - 560037',
    gstin: '29AAXFT4281F1ZH',
    udyamNo: '',
    bankAccounts: [
      {
        id: 1,
        bankName: 'CANARA BANK',
        accountName: 'Tomato Cafe',
        accountNumber: '120033270182',
        ifscCode: 'CNRB0000684',
        branch: '684- Bangalore LalBagh West 560004',
        customTitle: ''
      }
    ]
  });

  const [editingSection, setEditingSection] = useState(null);
  const [editingBankId, setEditingBankId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [facilityModalOpen, setFacilityModalOpen] = useState(false);
  const [facilityEditId, setFacilityEditId] = useState(null);
  const [facilityInput, setFacilityInput] = useState('');
  const [facilityLoading, setFacilityLoading] = useState(false);
  
  // Expense Categories state
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [categoryEditId, setCategoryEditId] = useState(null);
  const [categoryInput, setCategoryInput] = useState('');
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [signaturePreviewOpen, setSignaturePreviewOpen] = useState(false);

  // Invoice Categories state
  const [invoiceCategories, setInvoiceCategories] = useState([]);
  const [invoiceCategoryModalOpen, setInvoiceCategoryModalOpen] = useState(false);
  const [invoiceCategoryEditId, setInvoiceCategoryEditId] = useState(null);
  const [invoiceCategoryInput, setInvoiceCategoryInput] = useState('');
  const [invoiceCategoryLoading, setInvoiceCategoryLoading] = useState(false);

  // Departments state
  const [departments, setDepartments] = useState([]);
  const [departmentModalOpen, setDepartmentModalOpen] = useState(false);
  const [departmentEditId, setDepartmentEditId] = useState(null);
  const [departmentInput, setDepartmentInput] = useState('');
  const [departmentLoading, setDepartmentLoading] = useState(false);

  // Invoice Rules state
  const [invoiceRules, setInvoiceRules] = useState([]);
  const [invoiceRuleModalOpen, setInvoiceRuleModalOpen] = useState(false);
  const [invoiceRuleEditId, setInvoiceRuleEditId] = useState(null);
  const [selectedFacilityId, setSelectedFacilityId] = useState('');
  const [selectedInvoiceCategoryId, setSelectedInvoiceCategoryId] = useState('');
  const [selectedInvoiceTypeId, setSelectedInvoiceTypeId] = useState('');
  const [selectedBankAccountId, setSelectedBankAccountId] = useState('');
  const [invoiceRuleLoading, setInvoiceRuleLoading] = useState(false);

  // Invoice Types state
  const [invoiceTypes, setInvoiceTypes] = useState([]);
  const [invoiceTypeModalOpen, setInvoiceTypeModalOpen] = useState(false);
  const [invoiceTypeEditId, setInvoiceTypeEditId] = useState(null);
  const [invoiceTypeInput, setInvoiceTypeInput] = useState('');
  const [invoiceTypeLoading, setInvoiceTypeLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('company-details');

  // Check if current user is admin
  const isAdmin = currentUser?.role === 'admin';

  // Sync facilities prop with local state
  useEffect(() => {
    if (facilitiesProp && facilitiesProp.length > 0) {
      setFacilitiesLocal(facilitiesProp);
    }
  }, [facilitiesProp]);

  // Ensure facilities are loaded when configurations page mounts
  useEffect(() => {
    const ensureFacilitiesLoaded = async () => {
      try {
        // Always fetch facilities to ensure they're up to date
        const list = await fetchFacilities();
        const facilitiesList = Array.isArray(list) ? list : [];
        setFacilitiesLocal(facilitiesList);
        // Also update parent if setFacilities is provided
        if (setFacilities) {
          setFacilities(facilitiesList);
        }
      } catch (err) {
        // Fail silently here; other parts of the app can still function
        console.error('Failed to load facilities for configurations:', err);
      }
    };
    ensureFacilitiesLoaded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  

  // Load configuration from database on component mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        setLoading(true);
        const savedConfig = await fetchConfig();
        setConfig(savedConfig);
      } catch (error) {
        console.error('Failed to load config:', error);
        // Keep default config if loading fails
      } finally {
        setLoading(false);
      }
    };
    loadConfig();
  }, []);

  // Load expense categories
  useEffect(() => {
    const loadExpenseCategories = async () => {
      try {
        const categories = await fetchExpenseCategoriesWithUsage();
        setExpenseCategories(categories || []);
      } catch (error) {
        setExpenseCategories([]);
      }
    };
    loadExpenseCategories();
  }, []);

  // Load invoice categories
  useEffect(() => {
    const loadInvoiceCategories = async () => {
      try {
        const categories = await fetchInvoiceCategories();
        setInvoiceCategories(Array.isArray(categories) ? categories : []);
      } catch (error) {
        setInvoiceCategories([]);
      }
    };
    loadInvoiceCategories();
  }, []);

  // Load departments
  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const list = await fetchDepartments();
        setDepartments(Array.isArray(list) ? list : []);
      } catch (error) {
        setDepartments([]);
      }
    };
    loadDepartments();
  }, []);

  // Load invoice rules
  useEffect(() => {
    const loadInvoiceRules = async () => {
      try {
        const rules = await fetchInvoiceRules();
        setInvoiceRules(Array.isArray(rules) ? rules : []);
      } catch (error) {
        setInvoiceRules([]);
      }
    };
    loadInvoiceRules();
  }, []);

  // Load invoice types
  useEffect(() => {
    const loadInvoiceTypes = async () => {
      try {
        const types = await fetchInvoiceTypes();
        setInvoiceTypes(Array.isArray(types) ? types : []);
      } catch (error) {
        console.error('Failed to load invoice types:', error);
        setInvoiceTypes([]);
      }
    };
    loadInvoiceTypes();
  }, []);


  // Note: Configuration is saved manually when user clicks Save button
  // This prevents automatic saving on component mount

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await saveConfig(config);
      setEditingSection(null);
    } catch (error) {
      alert('Failed to save configuration. Please try again.');
    }
  };

  const handleInputChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  // Payment terms handlers
  const handlePaymentTermChange = (id, field, value) => {
    setConfig(prev => {
      const existing = Array.isArray(prev.paymentTerms) ? prev.paymentTerms : [];
      return {
        ...prev,
        paymentTerms: existing.map(term =>
          String(term.id) === String(id) ? { ...term, [field]: value } : term
        )
      };
    });
  };

  const addPaymentTerm = () => {
    setConfig(prev => {
      const existing = Array.isArray(prev.paymentTerms) ? prev.paymentTerms : [];
      const newId = Date.now();
      return {
        ...prev,
        paymentTerms: [
          ...existing,
          { id: newId, label: '', days: 0 }
        ]
      };
    });
  };

  const deletePaymentTerm = (id) => {
    setConfig(prev => {
      const existing = Array.isArray(prev.paymentTerms) ? prev.paymentTerms : [];
      return {
        ...prev,
        paymentTerms: existing.filter(term => String(term.id) !== String(id))
      };
    });
  };

  const handleSignatureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await uploadTermSheetSignature(file);
      const url = res.termSheetSignatureUrl;
      setConfig(prev => ({ ...prev, termSheetSignatureUrl: url }));
      // Persist config with new URL
      await saveConfig({ ...config, termSheetSignatureUrl: url });
      alert('Signature uploaded');
    } catch (err) {
      console.error('Signature upload error:', err);
      alert(err?.message || 'Failed to upload signature');
    }
  };

  const handleBankInputChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      bankAccounts: prev.bankAccounts.map(bank => 
        bank.id === editingBankId ? { ...bank, [field]: value } : bank
      )
    }));
  };

  const openAddBankAccountModal = () => {
    const newId = Math.max(...config.bankAccounts.map(b => b.id), 0) + 1;
    // Initialize the new bank account in config
    setConfig(prev => ({
      ...prev,
      bankAccounts: [...prev.bankAccounts, {
        id: newId,
        bankName: '',
        accountName: '',
        accountNumber: '',
        ifscCode: '',
        branch: '',
        customTitle: ''
      }]
    }));
    setEditingBankId(newId);
  };

  const handleBankAccountSave = async (e) => {
    e.preventDefault();
    try {
      await saveConfig(config);
      setEditingBankId(null);
    } catch (error) {
      alert('Failed to save bank account. Please try again.');
    }
  };

  const handleBankAccountCancel = () => {
    const bankAccount = config.bankAccounts.find(b => b.id === editingBankId);
    // If it's a new empty bank account, remove it
    if (bankAccount && !bankAccount.bankName && !bankAccount.accountName && !bankAccount.accountNumber && !bankAccount.ifscCode && !bankAccount.branch && !bankAccount.customTitle) {
      setConfig(prev => ({
        ...prev,
        bankAccounts: prev.bankAccounts.filter(b => b.id !== editingBankId)
      }));
    }
    setEditingBankId(null);
  };

  const deleteBankAccount = (bankId) => {
    if (window.confirm('Are you sure you want to delete this bank account?')) {
      setConfig(prev => ({
        ...prev,
        bankAccounts: prev.bankAccounts.filter(bank => bank.id !== bankId)
      }));
    }
  };

  const openAddFacilityModal = () => {
    setFacilityEditId(null);
    setFacilityInput('');
    setFacilityModalOpen(true);
  };

  const openEditFacilityModal = (facility) => {
    setFacilityEditId(facility.id);
    // Get facility name from DB - use name, facilityName, or fallback to ID
    const facilityName = facility.name || facility.facilityName || '';
    setFacilityInput(facilityName);
    setFacilityModalOpen(true);
  };

  const handleFacilitySave = async (e) => {
    e.preventDefault();
    setFacilityLoading(true);
    try {
      if (facilityEditId) {
        await updateFacility(facilityEditId, facilityInput);
      } else {
        await addFacility(facilityInput);
      }
      const updated = await fetchFacilities();
      setFacilitiesLocal(updated);
      if (setFacilities) setFacilities(updated);
      setFacilityModalOpen(false);
    } catch (err) {
      alert('Failed to save facility');
    } finally {
      setFacilityLoading(false);
    }
  };

  const handleFacilityDelete = async (id) => {
    if (!window.confirm('Delete this facility?')) return;
    setFacilityLoading(true);
    try {
      await deleteFacility(id);
      const updated = await fetchFacilities();
      setFacilitiesLocal(updated);
      if (setFacilities) setFacilities(updated);
    } catch (err) {
      alert('Failed to delete facility');
    } finally {
      setFacilityLoading(false);
    }
  };

  // Expense Categories functions
  const openAddCategoryModal = () => {
    setCategoryEditId(null);
    setCategoryInput('');
    setCategoryModalOpen(true);
  };

  const openEditCategoryModal = (category) => {
    setCategoryEditId(category._id);
    setCategoryInput(category.name);
    setCategoryModalOpen(true);
  };

  const handleCategorySave = async (e) => {
    e.preventDefault();
    setCategoryLoading(true);
    try {
      if (categoryEditId) {
        await updateExpenseCategory(categoryEditId, categoryInput);
      } else {
        await addExpenseCategory(categoryInput);
      }
      const updated = await fetchExpenseCategoriesWithUsage();
      setExpenseCategories(updated || []);
      setCategoryModalOpen(false);
      setCategoryInput('');
      setCategoryEditId(null);
    } catch (err) {
      const errorMessage = err?.message || 'Failed to save category';
      alert(`Failed to save category: ${errorMessage}`);
    } finally {
      setCategoryLoading(false);
    }
  };

  const handleCategoryDelete = async (category) => {
    if (!window.confirm(`Delete category "${category.name}"?`)) return;
    // Prevent delete if category is in use and show a helpful message
    if (typeof category.usageCount === 'number' && category.usageCount > 0) {
      alert(`Cannot delete category. ${category.usageCount} expense(s) are using this category.`);
      return;
    }
    setCategoryLoading(true);
    try {
      await deleteExpenseCategory(category._id);
      const updated = await fetchExpenseCategoriesWithUsage();
      setExpenseCategories(updated || []);
    } catch (err) {
      alert(err?.message || 'Failed to delete category');
    } finally {
      setCategoryLoading(false);
    }
  };

  // Invoice Categories functions
  const openAddInvoiceCategoryModal = () => {
    setInvoiceCategoryEditId(null);
    setInvoiceCategoryInput('');
    setInvoiceCategoryModalOpen(true);
  };

  const openEditInvoiceCategoryModal = (category) => {
    setInvoiceCategoryEditId(category.id);
    setInvoiceCategoryInput(category.name);
    setInvoiceCategoryModalOpen(true);
  };

  const handleInvoiceCategorySave = async (e) => {
    e.preventDefault();
    if (!invoiceCategoryInput.trim()) return;
    setInvoiceCategoryLoading(true);
    try {
      if (invoiceCategoryEditId) {
        await updateInvoiceCategory(invoiceCategoryEditId, invoiceCategoryInput.trim());
      } else {
        await addInvoiceCategory(invoiceCategoryInput.trim());
      }
      const updated = await fetchInvoiceCategories();
      setInvoiceCategories(Array.isArray(updated) ? updated : []);
      setInvoiceCategoryModalOpen(false);
      setInvoiceCategoryInput('');
      setInvoiceCategoryEditId(null);
    } catch (err) {
      const errorMessage = err?.message || 'Failed to save invoice category';
      alert(errorMessage);
    } finally {
      setInvoiceCategoryLoading(false);
    }
  };

  const handleInvoiceCategoryDelete = async (category) => {
    if (!window.confirm(`Delete invoice category "${category.name}"?`)) return;
    setInvoiceCategoryLoading(true);
    try {
      await deleteInvoiceCategory(category.id);
      const updated = await fetchInvoiceCategories();
      setInvoiceCategories(Array.isArray(updated) ? updated : []);
    } catch (err) {
      const errorMessage = err?.message || 'Failed to delete invoice category';
      alert(errorMessage);
    } finally {
      setInvoiceCategoryLoading(false);
    }
  };

  // Departments handlers
  const openAddDepartmentModal = () => {
    setDepartmentEditId(null);
    setDepartmentInput('');
    setDepartmentModalOpen(true);
  };

  const openEditDepartmentModal = (dept) => {
    setDepartmentEditId(dept.id);
    setDepartmentInput(dept.name);
    setDepartmentModalOpen(true);
  };

  const handleDepartmentSave = async (e) => {
    e.preventDefault();
    if (!departmentInput.trim()) return;
    setDepartmentLoading(true);
    try {
      if (departmentEditId) {
        await updateDepartment(departmentEditId, departmentInput.trim());
      } else {
        await addDepartment(departmentInput.trim());
      }
      const updated = await fetchDepartments();
      setDepartments(Array.isArray(updated) ? updated : []);
      setDepartmentModalOpen(false);
      setDepartmentInput('');
      setDepartmentEditId(null);
    } catch (err) {
      const errorMessage = err?.message || 'Failed to save department';
      alert(errorMessage);
    } finally {
      setDepartmentLoading(false);
    }
  };

  const handleDepartmentDelete = async (dept) => {
    if (!window.confirm(`Delete department "${dept.name}"?`)) return;
    setDepartmentLoading(true);
    try {
      await deleteDepartment(dept.id);
      const updated = await fetchDepartments();
      setDepartments(Array.isArray(updated) ? updated : []);
    } catch (err) {
      const errorMessage = err?.message || 'Failed to delete department';
      alert(errorMessage);
    } finally {
      setDepartmentLoading(false);
    }
  };

  // Invoice Rules handlers
  const openAddInvoiceRuleModal = () => {
    setInvoiceRuleEditId(null);
    setSelectedFacilityId('');
    setSelectedInvoiceCategoryId('');
    setSelectedInvoiceTypeId('');
    setSelectedBankAccountId('');
    setInvoiceRuleModalOpen(true);
  };

  const openEditInvoiceRuleModal = (rule) => {
    setInvoiceRuleEditId(rule.id);
    setSelectedFacilityId(rule.facilityId);
    setSelectedInvoiceCategoryId(rule.invoiceCategoryId);
    setSelectedInvoiceTypeId(rule.invoiceTypeId || '');
    setSelectedBankAccountId(rule.bankAccountId);
    setInvoiceRuleModalOpen(true);
  };

  const handleInvoiceRuleSave = async (e) => {
    e.preventDefault();
    if (!selectedFacilityId || !selectedInvoiceCategoryId || !selectedInvoiceTypeId || !selectedBankAccountId) {
      alert('Please select Facility, Invoice Category, Invoice Type, and Bank Account');
      return;
    }
    setInvoiceRuleLoading(true);
    try {
      const ruleData = {
        facilityId: selectedFacilityId,
        invoiceCategoryId: selectedInvoiceCategoryId,
        invoiceTypeId: selectedInvoiceTypeId,
        bankAccountId: selectedBankAccountId
      };
      if (invoiceRuleEditId) {
        await updateInvoiceRule(invoiceRuleEditId, ruleData);
      } else {
        await addInvoiceRule(ruleData);
      }
      const updated = await fetchInvoiceRules();
      setInvoiceRules(Array.isArray(updated) ? updated : []);
      setInvoiceRuleModalOpen(false);
      setSelectedFacilityId('');
      setSelectedInvoiceCategoryId('');
      setSelectedBankAccountId('');
      setInvoiceRuleEditId(null);
    } catch (err) {
      const errorMessage = err?.message || 'Failed to save invoice rule';
      alert(errorMessage);
    } finally {
      setInvoiceRuleLoading(false);
    }
  };

  const handleInvoiceRuleDelete = async (rule) => {
    if (!window.confirm('Delete this invoice rule?')) return;
    setInvoiceRuleLoading(true);
    try {
      await deleteInvoiceRule(rule.id);
      const updated = await fetchInvoiceRules();
      setInvoiceRules(Array.isArray(updated) ? updated : []);
    } catch (err) {
      const errorMessage = err?.message || 'Failed to delete invoice rule';
      alert(errorMessage);
    } finally {
      setInvoiceRuleLoading(false);
    }
  };

  // Helper functions to get names from IDs
  const getFacilityName = (facilityId) => {
    const facility = facilities.find(f => String(f.id) === String(facilityId));
    if (!facility) return 'Unknown Facility';
    return facility.name || facility.facilityName || `Facility ${facilityId}`;
  };

  const getInvoiceCategoryName = (categoryId) => {
    const category = invoiceCategories.find(c => String(c.id) === String(categoryId));
    return category ? category.name : 'Unknown Category';
  };

  const getBankAccountName = (bankAccountId) => {
    const bankAccount = config.bankAccounts.find(b => String(b.id) === String(bankAccountId));
    if (!bankAccount) return 'Unknown Bank Account';
    // Use custom title if available, otherwise fall back to "Bank Name - Account Name"
    if (bankAccount.customTitle && bankAccount.customTitle.trim() !== '') {
      return bankAccount.customTitle;
    }
    return `${bankAccount.bankName} - ${bankAccount.accountName}`;
  };

  const getInvoiceTypeName = (invoiceTypeId) => {
    const invoiceType = invoiceTypes.find(t => String(t.id) === String(invoiceTypeId));
    return invoiceType ? invoiceType.name : 'Unknown Invoice Type';
  };

  // Invoice Types handlers
  const openAddInvoiceTypeModal = () => {
    setInvoiceTypeEditId(null);
    setInvoiceTypeInput('');
    setInvoiceTypeModalOpen(true);
  };

  const openEditInvoiceTypeModal = (type) => {
    setInvoiceTypeEditId(type.id);
    setInvoiceTypeInput(type.name);
    setInvoiceTypeModalOpen(true);
  };

  const handleInvoiceTypeSave = async (e) => {
    e.preventDefault();
    if (!invoiceTypeInput.trim()) return;
    setInvoiceTypeLoading(true);
    try {
      if (invoiceTypeEditId) {
        await updateInvoiceType(invoiceTypeEditId, invoiceTypeInput.trim());
      } else {
        await addInvoiceType(invoiceTypeInput.trim());
      }
      const updated = await fetchInvoiceTypes();
      setInvoiceTypes(Array.isArray(updated) ? updated : []);
      setInvoiceTypeModalOpen(false);
      setInvoiceTypeInput('');
      setInvoiceTypeEditId(null);
    } catch (err) {
      const errorMessage = err?.message || 'Failed to save invoice type';
      alert(errorMessage);
    } finally {
      setInvoiceTypeLoading(false);
    }
  };

  const handleInvoiceTypeDelete = async (type) => {
    if (!window.confirm(`Delete invoice type "${type.name}"?`)) return;
    setInvoiceTypeLoading(true);
    try {
      await deleteInvoiceType(type.id);
      const updated = await fetchInvoiceTypes();
      setInvoiceTypes(Array.isArray(updated) ? updated : []);
    } catch (err) {
      const errorMessage = err?.message || 'Failed to delete invoice type';
      alert(errorMessage);
    } finally {
      setInvoiceTypeLoading(false);
    }
  };

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
  };

  const renderEditModal = () => {
    if (!editingSection) return null;

    const section = editingSection;
    const isBank = section.startsWith('bank');

    return (
      <div className="modal-right-overlay">
        <div className="modal-right-content" onClick={e => e.stopPropagation()}>
          <div className="modal-right-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#333' }}>
                Edit {section === 'company' ? 'Company Details' : 
                      section === 'invoice' ? 'Invoice Settings' :
                      section === 'quote' ? 'Quote Settings' :
                      section === 'tax' ? 'Tax settings for rent invoice' : 'Bank Account'}
              </h2>
              <button
                className="btn"
                style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#888', cursor: 'pointer', padding: 0 }}
                onClick={() => setEditingSection(null)}
                aria-label="Close"
              >
                <X />
              </button>
            </div>
          </div>
          <div className="modal-right-body">
            <form onSubmit={handleSubmit}>
              {section === 'company' && (
                <div>
                  <div className="form-group">
                    <label>Company Name</label>
                    <input
                      type="text"
                      value={config.companyName}
                      onChange={(e) => handleInputChange('companyName', e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Company Address</label>
                    <textarea
                      value={config.companyAddress}
                      onChange={(e) => handleInputChange('companyAddress', e.target.value)}
                      required
                      rows="3"
                    />
                  </div>
                  <div className="form-group">
                    <label>GSTIN</label>
                    <input
                      type="text"
                      value={config.gstin}
                      onChange={(e) => handleInputChange('gstin', e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>UDYAM Number</label>
                    <input
                      type="text"
                      value={config.udyamNo}
                      onChange={(e) => handleInputChange('udyamNo', e.target.value)}
                    />
                  </div>
                </div>
              )}
              {section === 'invoice' && (
                <div>
                  <div className="form-group">
                    <label>Next Invoice Number</label>
                    <input
                      type="number"
                      value={config.nextInvoiceNumber || 1}
                      onChange={(e) => handleInputChange('nextInvoiceNumber', parseInt(e.target.value))}
                      min="1"
                      required
                    />
                    <small style={{ color: '#666', fontSize: '12px' }}>
                      This will be the number for the next invoice that gets generated.<br/>
                      <strong>Warning:</strong> Changing this number will affect all future invoices.
                    </small>
                  </div>
                  <div className="form-group">
                    <label>Next Credit Note Number</label>
                    <input
                      type="number"
                      value={config.nextCreditNoteNumber || 1}
                      onChange={(e) => handleInputChange('nextCreditNoteNumber', parseInt(e.target.value))}
                      min="1"
                      required
                    />
                    <small style={{ color: '#666', fontSize: '12px' }}>
                      This will be the number for the next credit note that gets generated.<br/>
                      <strong>Warning:</strong> Changing this number will affect all future credit notes.
                    </small>
                  </div>
                  <div className="form-group">
                    <label>Invoice Number Format</label>
                    <input
                      type="text"
                      value={config.invoiceNumberFormat || 'YYYY-YYYY/###'}
                      onChange={(e) => handleInputChange('invoiceNumberFormat', e.target.value)}
                      placeholder="YYYY-YYYY/###"
                      required
                    />
                    <small style={{ color: '#666', fontSize: '12px' }}>
                      Format: YYYY-YYYY for financial year, ### for invoice number (e.g., 2024-2025/001)
                    </small>
                  </div>
                  <div className="form-group">
                    <label>Default Place of Supply</label>
                    <input
                      type="text"
                      value={config.defaultPlaceOfSupply || 'Karnataka'}
                      onChange={(e) => handleInputChange('defaultPlaceOfSupply', e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Payment Terms (Due Date)</label>
                    <div style={{ marginBottom: 8, fontSize: 12, color: '#666' }}>
                      Configure reusable due date options like <strong>NET 10</strong>, <strong>NET 15</strong>, etc.
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={addPaymentTerm}
                      >
                        <Plus size="1em" style={{ marginRight: 6 }} />Add Payment Term
                      </button>
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
                          <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 12 }}>Label</th>
                          <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 12, width: 120 }}>Days</th>
                          <th style={{ padding: '8px 10px', textAlign: 'center', fontSize: 12, width: 60 }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(Array.isArray(config.paymentTerms) ? config.paymentTerms : []).map(term => (
                          <tr key={term.id} style={{ borderBottom: '1px solid #f1f1f1' }}>
                            <td style={{ padding: '8px 10px' }}>
                              <input
                                type="text"
                                placeholder="NET 10"
                                value={term.label || ''}
                                onChange={(e) => handlePaymentTermChange(term.id, 'label', e.target.value)}
                                style={{ width: '100%' }}
                                required
                              />
                            </td>
                            <td style={{ padding: '8px 10px' }}>
                              <input
                                type="number"
                                min="0"
                                value={term.days ?? ''}
                                onChange={(e) => handlePaymentTermChange(term.id, 'days', parseInt(e.target.value || '0', 10))}
                                style={{ width: '100%' }}
                                required
                              />
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                              <button
                                type="button"
                                className="btn btn-danger"
                                style={{ padding: '4px 6px', fontSize: 12 }}
                                onClick={() => deletePaymentTerm(term.id)}
                              >
                                <Trash size="1em" />
                              </button>
                            </td>
                          </tr>
                        ))}
                        {(!config.paymentTerms || config.paymentTerms.length === 0) && (
                          <tr>
                            <td colSpan={3} style={{ padding: '8px 10px', fontSize: 12, color: '#888', fontStyle: 'italic' }}>
                              No payment terms configured. Click "Add Payment Term" to create one.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {section === 'quote' && (
                <div>
                  <div className="form-group">
                    <label>Quote Prefix</label>
                    <input
                      type="text"
                      value={config.quotePrefix || 'QUO'}
                      onChange={(e) => handleInputChange('quotePrefix', e.target.value)}
                      placeholder="QUO"
                      required
                    />
                    <small style={{ color: '#666', fontSize: '12px' }}>
                      This prefix will be used in quote numbers (e.g., QUO-SEPT-001)
                    </small>
                  </div>
                  <div className="form-group">
                    <label>Next Quote Number</label>
                    <input
                      type="number"
                      value={config.nextQuoteNumber || 1}
                      onChange={(e) => handleInputChange('nextQuoteNumber', parseInt(e.target.value))}
                      min="1"
                      required
                    />
                    <small style={{ color: '#666', fontSize: '12px' }}>
                      This will be the number for the next quote that gets generated.<br/>
                      <strong>Warning:</strong> Changing this number will affect all future quotes.
                    </small>
                  </div>
                </div>
              )}
              {section === 'tax' && (
                <div>
                  <div className="form-group">
                    <label>CGST Rate (%)</label>
                    <input
                      type="number"
                      value={config.cgstRate || 0}
                      onChange={(e) => handleInputChange('cgstRate', parseFloat(e.target.value))}
                      step="0.01"
                      min="0"
                      max="100"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>SGST Rate (%)</label>
                    <input
                      type="number"
                      value={config.sgstRate || 0}
                      onChange={(e) => handleInputChange('sgstRate', parseFloat(e.target.value))}
                      step="0.01"
                      min="0"
                      max="100"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>IGST Rate (%)</label>
                    <input
                      type="number"
                      value={config.igstRate || 0}
                      onChange={(e) => handleInputChange('igstRate', parseFloat(e.target.value))}
                      step="0.01"
                      min="0"
                      max="100"
                      required
                    />
                  </div>
                </div>
              )}
              {isBank && (
                <div>
                  <div className="form-group">
                    <label>Custom Title</label>
                    <input
                      type="text"
                      value={config.bankAccounts.find(b => b.id === editingBankId)?.customTitle || ''}
                      onChange={(e) => handleBankInputChange('customTitle', e.target.value)}
                      placeholder="Optional custom title for this bank account"
                    />
                  </div>
                  <div className="form-group">
                    <label>Bank Name</label>
                    <input
                      type="text"
                      value={config.bankAccounts.find(b => b.id === editingBankId)?.bankName || ''}
                      onChange={(e) => handleBankInputChange('bankName', e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Account Name</label>
                    <input
                      type="text"
                      value={config.bankAccounts.find(b => b.id === editingBankId)?.accountName || ''}
                      onChange={(e) => handleBankInputChange('accountName', e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Account Number</label>
                    <input
                      type="text"
                      value={config.bankAccounts.find(b => b.id === editingBankId)?.accountNumber || ''}
                      onChange={(e) => handleBankInputChange('accountNumber', e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>IFSC Code</label>
                    <input
                      type="text"
                      value={config.bankAccounts.find(b => b.id === editingBankId)?.ifscCode || ''}
                      onChange={(e) => handleBankInputChange('ifscCode', e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Branch</label>
                    <input
                      type="text"
                      value={config.bankAccounts.find(b => b.id === editingBankId)?.branch || ''}
                      onChange={(e) => handleBankInputChange('branch', e.target.value)}
                      required
                    />
                  </div>
                </div>
              )}
            </form>
          </div>
          <div className="modal-right-footer">
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setEditingSection(null)}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" onClick={handleSubmit}>
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderBankModal = () => {
    if (!editingBankId) return null;

    return (
      <div className="modal-right-overlay">
        <div className="modal-right-content" onClick={e => e.stopPropagation()}>
          <div className="modal-right-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#333' }}>
                {config.bankAccounts.find(b => b.id === editingBankId) ? 'Edit Bank Account' : 'Add Bank Account'}
              </h2>
              <button
                className="btn"
                style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#888', cursor: 'pointer', padding: 0 }}
                onClick={handleBankAccountCancel}
                aria-label="Close"
              >
                <X />
              </button>
            </div>
          </div>
          <div className="modal-right-body">
            <form onSubmit={handleBankAccountSave}>
              <div className="form-group">
                <label>Custom Title</label>
                <input
                  type="text"
                  value={config.bankAccounts.find(b => b.id === editingBankId)?.customTitle || ''}
                  onChange={(e) => handleBankInputChange('customTitle', e.target.value)}
                  placeholder="Optional custom title for this bank account"
                />
              </div>
              <div className="form-group">
                <label>Bank Name</label>
                <input
                  type="text"
                  value={config.bankAccounts.find(b => b.id === editingBankId)?.bankName || ''}
                  onChange={(e) => handleBankInputChange('bankName', e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Account Name</label>
                <input
                  type="text"
                  value={config.bankAccounts.find(b => b.id === editingBankId)?.accountName || ''}
                  onChange={(e) => handleBankInputChange('accountName', e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Account Number</label>
                <input
                  type="text"
                  value={config.bankAccounts.find(b => b.id === editingBankId)?.accountNumber || ''}
                  onChange={(e) => handleBankInputChange('accountNumber', e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>IFSC Code</label>
                <input
                  type="text"
                  value={config.bankAccounts.find(b => b.id === editingBankId)?.ifscCode || ''}
                  onChange={(e) => handleBankInputChange('ifscCode', e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Branch</label>
                <input
                  type="text"
                  value={config.bankAccounts.find(b => b.id === editingBankId)?.branch || ''}
                  onChange={(e) => handleBankInputChange('branch', e.target.value)}
                  required
                />
              </div>
            </form>
          </div>
          <div className="modal-right-footer">
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary" onClick={handleBankAccountCancel}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" onClick={handleBankAccountSave}>
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div>Loading configuration...</div>;
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-title">
          <h1>System Configuration</h1>
          <p>{isAdmin 
            ? 'Manage company details, tax settings, bank accounts, and facilities.'
            : 'View company details, tax settings, bank accounts, and facilities. (Admin access required for editing)'
          }</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '24px', marginTop: '20px' }}>
        {/* Sidebar Menu */}
        <div style={{
          width: '240px',
          flexShrink: 0,
          background: 'white',
          borderRadius: '10px',
          padding: '16px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          height: 'fit-content',
          position: 'sticky',
          top: '20px'
        }}>
          <h3 style={{ 
            margin: '0 0 16px 0', 
            fontSize: '16px', 
            fontWeight: '600', 
            color: '#333',
            paddingBottom: '12px',
            borderBottom: '1px solid #eee'
          }}>
            Sections
          </h3>
          <nav style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                style={{
                  textAlign: 'left',
                  padding: '10px 12px',
                  border: 'none',
                  background: activeSection === item.id ? '#f0f4ff' : 'transparent',
                  color: activeSection === item.id ? '#3b82f6' : '#333',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: activeSection === item.id ? '500' : '400',
                  transition: 'all 0.2s ease',
                  borderLeft: activeSection === item.id ? '3px solid #3b82f6' : '3px solid transparent'
                }}
                onMouseEnter={(e) => {
                  if (activeSection !== item.id) {
                    e.target.style.background = '#f8f9fa';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeSection !== item.id) {
                    e.target.style.background = 'transparent';
                  }
                }}
              >
                {item.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Main Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Company Details Section */}
          {activeSection === 'company-details' && (
          <div style={{ background: 'white', padding: '32px', borderRadius: '10px', minHeight: 'calc(100vh - 200px)', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <div style={{ marginBottom: '32px', paddingBottom: '20px', borderBottom: '2px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#333' }}>Company Details</h2>
            <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#666' }}>Manage your company information and details</p>
          </div>
          {isAdmin && (
            <button className="btn btn-secondary" onClick={() => setEditingSection('company')}>
              <Edit size="1em" style={{marginRight: 6}}/>Edit
            </button>
          )}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '16px', fontWeight: '600', color: '#374151', width: '200px', verticalAlign: 'top' }}>Company Name:</td>
              <td style={{ padding: '16px', color: '#111827' }}>{config.companyName}</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '16px', fontWeight: '600', color: '#374151', width: '200px', verticalAlign: 'top' }}>GSTIN:</td>
              <td style={{ padding: '16px', color: '#111827' }}>{config.gstin}</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '16px', fontWeight: '600', color: '#374151', width: '200px', verticalAlign: 'top' }}>Address:</td>
              <td style={{ padding: '16px', color: '#111827' }}>{config.companyAddress}</td>
            </tr>
            {config.udyamNo && (
              <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '16px', fontWeight: '600', color: '#374151', width: '200px', verticalAlign: 'top' }}>UDYAM Number:</td>
                <td style={{ padding: '16px', color: '#111827' }}>{config.udyamNo}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
          )}

          {/* Invoice Settings Section */}
          {activeSection === 'invoice-settings' && (
          <div style={{ background: 'white', padding: '32px', borderRadius: '10px', minHeight: 'calc(100vh - 200px)', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <div style={{ marginBottom: '32px', paddingBottom: '20px', borderBottom: '2px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#333' }}>Invoice Settings</h2>
            <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#666' }}>Configure invoice numbering and format settings</p>
          </div>
          {isAdmin && (
            <button className="btn btn-secondary" onClick={() => setEditingSection('invoice')}>
              <Edit size="1em" style={{marginRight: 6}}/>Edit
            </button>
          )}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '16px', fontWeight: '600', color: '#374151', width: '250px', verticalAlign: 'top' }}>Next Invoice Number:</td>
              <td style={{ padding: '16px', color: '#111827' }}>{config.nextInvoiceNumber || 1}</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '16px', fontWeight: '600', color: '#374151', width: '250px', verticalAlign: 'top' }}>Next Credit Note Number:</td>
              <td style={{ padding: '16px', color: '#111827' }}>{config.nextCreditNoteNumber || 1}</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '16px', fontWeight: '600', color: '#374151', width: '250px', verticalAlign: 'top' }}>Invoice Number Format:</td>
              <td style={{ padding: '16px', color: '#111827' }}>
                {config.invoiceNumberFormat || 'YYYY-YYYY/###'}
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  Example: {(() => {
                    const currentYear = new Date().getFullYear();
                    const currentMonth = new Date().getMonth() + 1;
                    const financialYear = currentMonth >= 4 ? `${currentYear}-${currentYear + 1}` : `${currentYear - 1}-${currentYear}`;
                    return formatInvoiceNumber(config.nextInvoiceNumber || 1, financialYear, config);
                  })()}
                </div>
              </td>
            </tr>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '16px', fontWeight: '600', color: '#374151', width: '250px', verticalAlign: 'top' }}>Default Place of Supply:</td>
              <td style={{ padding: '16px', color: '#111827' }}>{config.defaultPlaceOfSupply || 'Karnataka'}</td>
            </tr>
          </tbody>
        </table>
      </div>
          )}

          {/* Quote Settings Section */}
          {activeSection === 'quote-settings' && (
          <div style={{ background: 'white', padding: '32px', borderRadius: '10px', minHeight: 'calc(100vh - 200px)', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <div style={{ marginBottom: '32px', paddingBottom: '20px', borderBottom: '2px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#333' }}>Quote Settings</h2>
            <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#666' }}>Configure quote numbering and prefix settings</p>
          </div>
          {isAdmin && (
            <button className="btn btn-secondary" onClick={() => setEditingSection('quote')}>
              <Edit size="1em" style={{marginRight: 6}}/>Edit
            </button>
          )}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '16px', fontWeight: '600', color: '#374151', width: '250px', verticalAlign: 'top' }}>Quote Prefix:</td>
              <td style={{ padding: '16px', color: '#111827' }}>{config.quotePrefix || 'QUO'}</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '16px', fontWeight: '600', color: '#374151', width: '250px', verticalAlign: 'top' }}>Next Quote Number:</td>
              <td style={{ padding: '16px', color: '#111827' }}>
                {config.nextQuoteNumber || 1}
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  Example: {(() => {
                    const month = new Date().toLocaleString('en-IN', { month: 'short' }).toUpperCase();
                    const n = (config?.nextQuoteNumber || 1).toString().padStart(3, '0');
                    const prefix = config?.quotePrefix || 'QUO';
                    return `${prefix}-${month}-${n}`;
                  })()}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
          )}

          {/* Term Sheet Signature Section */}
          {activeSection === 'term-sheet-signature' && (
          <div style={{ background: 'white', padding: '32px', borderRadius: '10px', minHeight: 'calc(100vh - 200px)', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <div style={{ marginBottom: '32px', paddingBottom: '20px', borderBottom: '2px solid #f0f0f0' }}>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#333' }}>Term Sheet Signature</h2>
          <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#666' }}>Upload and manage signature for term sheets</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {config.termSheetSignatureUrl ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <div style={{ 
                border: '2px solid #e1e5ee', 
                borderRadius: '8px', 
                padding: '12px', 
                background: '#fafafa',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onClick={() => setSignaturePreviewOpen(true)}
              onMouseEnter={(e) => e.target.style.borderColor = '#3b82f6'}
              onMouseLeave={(e) => e.target.style.borderColor = '#e1e5ee'}
              >
                <img 
                  src={config.termSheetSignatureUrl} 
                  alt="Signature Preview" 
                  style={{ 
                    maxHeight: 120, 
                    maxWidth: 300, 
                    objectFit: 'contain',
                    display: 'block'
                  }}
                  onError={(e) => {
                    console.error('Failed to load signature image:', config.termSheetSignatureUrl);
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <div style={{ fontSize: '12px', color: '#666', textAlign: 'center' }}>
                Click to view full size
              </div>
            </div>
          ) : (
            <div style={{ 
              color: '#888', 
              fontStyle: 'italic',
              padding: '20px',
              border: '2px dashed #ddd',
              borderRadius: '8px',
              background: '#f9f9f9'
            }}>
              No signature uploaded
            </div>
          )}
          {isAdmin && (
            <label className="btn btn-primary" style={{ cursor: 'pointer' }}>
              Upload Signature
              <input type="file" accept="image/*" onChange={handleSignatureUpload} style={{ display: 'none' }} />
            </label>
          )}
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
          This signature will be used across all Term Sheets.
        </div>
      </div>
          )}

          {/* Tax Settings Section */}
          {activeSection === 'tax-settings' && (
          <div style={{ background: 'white', padding: '32px', borderRadius: '10px', minHeight: 'calc(100vh - 200px)', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <div style={{ marginBottom: '32px', paddingBottom: '20px', borderBottom: '2px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#333' }}>Tax Settings</h2>
            <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#666' }}>Configure tax rates for rent invoices</p>
          </div>
          {isAdmin && (
            <button className="btn btn-secondary" onClick={() => setEditingSection('tax')}>
              <Edit size="1em" style={{marginRight: 6}}/>Edit
            </button>
          )}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '16px', fontWeight: '600', color: '#374151', width: '250px', verticalAlign: 'top' }}>CGST Rate:</td>
              <td style={{ padding: '16px', color: '#111827' }}>{config.cgstRate || 0}%</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '16px', fontWeight: '600', color: '#374151', width: '250px', verticalAlign: 'top' }}>SGST Rate:</td>
              <td style={{ padding: '16px', color: '#111827' }}>{config.sgstRate || 0}%</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '16px', fontWeight: '600', color: '#374151', width: '250px', verticalAlign: 'top' }}>IGST Rate:</td>
              <td style={{ padding: '16px', color: '#111827' }}>{config.igstRate || 0}%</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '16px', fontWeight: '600', color: '#374151', width: '250px', verticalAlign: 'top' }}>Place of Supply:</td>
              <td style={{ padding: '16px', color: '#111827' }}>{config.defaultPlaceOfSupply || 'Karnataka'}</td>
            </tr>
          </tbody>
        </table>
      </div>
          )}

          {/* Bank Accounts Section */}
          {activeSection === 'bank-accounts' && (
          <div style={{ background: 'white', padding: '32px', borderRadius: '10px', minHeight: 'calc(100vh - 200px)', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <div style={{ marginBottom: '32px', paddingBottom: '20px', borderBottom: '2px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#333' }}>Bank Accounts</h2>
            <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#666' }}>Manage bank account information</p>
          </div>
          {isAdmin && (
            <button className="btn btn-primary" onClick={openAddBankAccountModal}>
              <Plus size="1em" style={{marginRight: 6}}/>Add Bank Account
            </button>
          )}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Custom Title</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Bank Name</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Account Name</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Account Number</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '14px' }}>IFSC Code</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Branch</th>
              {isAdmin && (
                <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '14px', width: '120px' }}>Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {config.bankAccounts.map((bank, index) => (
              <tr key={bank.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '16px', color: '#111827' }}>{bank.customTitle || '-'}</td>
                <td style={{ padding: '16px', color: '#111827' }}>{bank.bankName}</td>
                <td style={{ padding: '16px', color: '#111827' }}>{bank.accountName}</td>
                <td style={{ padding: '16px', color: '#111827' }}>{bank.accountNumber}</td>
                <td style={{ padding: '16px', color: '#111827' }}>{bank.ifscCode}</td>
                <td style={{ padding: '16px', color: '#111827' }}>{bank.branch}</td>
                {isAdmin && (
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                      <button 
                        className="btn btn-secondary" 
                        style={{ padding: '6px 8px', fontSize: '12px' }}
                        onClick={() => setEditingBankId(bank.id)}
                        title="Edit"
                      >
                        <Edit size="1em" />
                      </button>
                      <button 
                        className="btn btn-danger" 
                        style={{ padding: '6px 8px', fontSize: '12px' }}
                        onClick={() => deleteBankAccount(bank.id)}
                        title="Delete"
                      >
                        <Trash size="1em" />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
          )}

          {/* Invoice Categories Section */}
          {activeSection === 'invoice-categories' && (
          <div style={{ background: 'white', padding: '32px', borderRadius: '10px', minHeight: 'calc(100vh - 200px)', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <div style={{ marginBottom: '32px', paddingBottom: '20px', borderBottom: '2px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#333' }}>Invoice Categories</h2>
            <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#666' }}>Manage invoice categories for organizing invoices</p>
          </div>
          {isAdmin && (
            <button className="btn btn-primary" onClick={openAddInvoiceCategoryModal}>
              <Plus size="1em" style={{ marginRight: 6 }} />Add Category
            </button>
          )}
        </div>
        {invoiceCategories.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '32px 16px',
              color: '#718096',
              fontSize: '14px',
              fontStyle: 'italic',
            }}
          >
            No invoice categories defined yet. Use categories to label and group invoices.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Category Name</th>
                {isAdmin && (
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '14px', width: '120px' }}>Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {invoiceCategories.map((category) => (
                <tr key={category.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '16px', color: '#111827' }}>{category.name}</td>
                  {isAdmin && (
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button
                          className="btn btn-secondary"
                          style={{ padding: '6px 8px', fontSize: '12px' }}
                          onClick={() => openEditInvoiceCategoryModal(category)}
                          title="Edit Invoice Category"
                        >
                          <Edit size="1em" />
                        </button>
                        <button
                          className="btn btn-danger"
                          style={{ padding: '6px 8px', fontSize: '12px' }}
                          onClick={() => handleInvoiceCategoryDelete(category)}
                          title="Delete Invoice Category"
                        >
                          <Trash size="1em" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
          )}

          {/* Departments Section */}
          {activeSection === 'departments' && (
          <div style={{ background: 'white', padding: '32px', borderRadius: '10px', minHeight: 'calc(100vh - 200px)', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <div style={{ marginBottom: '32px', paddingBottom: '20px', borderBottom: '2px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#333' }}>Departments</h2>
            <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#666' }}>Manage departments for tagging and organizing internal procurement and invoices</p>
          </div>
          {isAdmin && (
            <button className="btn btn-primary" onClick={openAddDepartmentModal}>
              <Plus size="1em" style={{ marginRight: 6 }} />Add Department
            </button>
          )}
        </div>
        {departments.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '32px 16px',
              color: '#718096',
              fontSize: '14px',
              fontStyle: 'italic',
            }}
          >
            No departments defined yet. Add departments to organize internal procurement and adhoc invoices.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Department Name</th>
                {isAdmin && (
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '14px', width: '120px' }}>Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {departments.map((dept) => (
                <tr key={dept.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '16px', color: '#111827' }}>{dept.name}</td>
                  {isAdmin && (
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button
                          className="btn btn-secondary"
                          style={{ padding: '6px 8px', fontSize: '12px' }}
                          onClick={() => openEditDepartmentModal(dept)}
                          title="Edit Department"
                        >
                          <Edit size="1em" />
                        </button>
                        <button
                          className="btn btn-danger"
                          style={{ padding: '6px 8px', fontSize: '12px' }}
                          onClick={() => handleDepartmentDelete(dept)}
                          title="Delete Department"
                        >
                          <Trash size="1em" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
          )}

          {/* Invoice Rules Section */}
          {activeSection === 'invoice-rules' && (
          <div style={{ background: 'white', padding: '32px', borderRadius: '10px', minHeight: 'calc(100vh - 200px)', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <div style={{ marginBottom: '32px', paddingBottom: '20px', borderBottom: '2px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#333' }}>Invoice Rules</h2>
            <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#666' }}>Configure rules mapping facilities, categories, and bank accounts</p>
          </div>
          {isAdmin && (
            <button className="btn btn-primary" onClick={openAddInvoiceRuleModal}>
              <Plus size="1em" style={{ marginRight: 6 }} />Add Rule
            </button>
          )}
        </div>
        {invoiceRules.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '32px 16px',
              color: '#718096',
              fontSize: '14px',
              fontStyle: 'italic',
            }}
          >
            No invoice rules defined yet. Rules map facilities, categories, and bank accounts together.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Facility</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Invoice Category</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Invoice Type</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Bank Account</th>
                {isAdmin && (
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '14px', width: '120px' }}>Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {invoiceRules.map((rule) => (
                <tr key={rule.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '16px', color: '#111827' }}>{getFacilityName(rule.facilityId)}</td>
                  <td style={{ padding: '16px', color: '#111827' }}>{getInvoiceCategoryName(rule.invoiceCategoryId)}</td>
                  <td style={{ padding: '16px', color: '#111827' }}>{rule.invoiceTypeId ? getInvoiceTypeName(rule.invoiceTypeId) : '-'}</td>
                  <td style={{ padding: '16px', color: '#111827' }}>{getBankAccountName(rule.bankAccountId)}</td>
                  {isAdmin && (
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button
                          className="btn btn-secondary"
                          style={{ padding: '6px 8px', fontSize: '12px' }}
                          onClick={() => openEditInvoiceRuleModal(rule)}
                          title="Edit Rule"
                        >
                          <Edit size="1em" />
                        </button>
                        <button
                          className="btn btn-danger"
                          style={{ padding: '6px 8px', fontSize: '12px' }}
                          onClick={() => handleInvoiceRuleDelete(rule)}
                          title="Delete Rule"
                        >
                          <Trash size="1em" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
          )}

          {/* Invoice Types Section */}
          {activeSection === 'invoice-types' && (
          <div style={{ background: 'white', padding: '32px', borderRadius: '10px', minHeight: 'calc(100vh - 200px)', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <div style={{ marginBottom: '32px', paddingBottom: '20px', borderBottom: '2px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#333' }}>Invoice Types</h2>
            <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#666' }}>Manage different types of invoices</p>
          </div>
          {isAdmin && (
            <button className="btn btn-primary" onClick={openAddInvoiceTypeModal}>
              <Plus size="1em" style={{ marginRight: 6 }} />Add Invoice Type
            </button>
          )}
        </div>
        {invoiceTypes.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '32px 16px',
              color: '#718096',
              fontSize: '14px',
              fontStyle: 'italic',
            }}
          >
            No invoice types defined yet. Add invoice types to categorize your invoices.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Invoice Type</th>
                {isAdmin && (
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '14px', width: '120px' }}>Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {invoiceTypes.map((type) => (
                <tr key={type.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '16px', color: '#111827' }}>{type.name}</td>
                  {isAdmin && (
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button
                          className="btn btn-secondary"
                          style={{ padding: '6px 8px', fontSize: '12px' }}
                          onClick={() => openEditInvoiceTypeModal(type)}
                          title="Edit Invoice Type"
                        >
                          <Edit size="1em" />
                        </button>
                        <button
                          className="btn btn-danger"
                          style={{ padding: '6px 8px', fontSize: '12px' }}
                          onClick={() => handleInvoiceTypeDelete(type)}
                          title="Delete Invoice Type"
                        >
                          <Trash size="1em" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
          )}

          {/* Facilities Section */}
          {activeSection === 'facilities' && (
          <div style={{ background: 'white', padding: '32px', borderRadius: '10px', minHeight: 'calc(100vh - 200px)', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <div style={{ marginBottom: '32px', paddingBottom: '20px', borderBottom: '2px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#333' }}>Facilities</h2>
            <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#666' }}>Manage facility information</p>
          </div>
          {isAdmin && (
            <button className="btn btn-primary" onClick={openAddFacilityModal}>
              <Plus size="1em" style={{marginRight: 6}}/>Add Facility
            </button>
          )}
        </div>
        {facilities.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '32px 16px',
              color: '#718096',
              fontSize: '14px',
              fontStyle: 'italic',
            }}
          >
            No facilities defined yet. Add facilities to get started.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Facility Name</th>
                {isAdmin && (
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '14px', width: '120px' }}>Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {facilities.map(facility => (
                <tr key={facility.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '16px', color: '#111827' }}>{facility.name || facility.facilityName || `Facility ${facility.id}`}</td>
                  {isAdmin && (
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button 
                          className="btn btn-secondary" 
                          style={{ padding: '6px 8px', fontSize: '12px' }}
                          onClick={() => openEditFacilityModal(facility)}
                          title="Edit Facility"
                        >
                          <Edit size="1em" />
                        </button>
                        <button 
                          className="btn btn-danger" 
                          style={{ padding: '6px 8px', fontSize: '12px' }}
                          onClick={() => handleFacilityDelete(facility.id)}
                          title="Delete Facility"
                        >
                          <Trash size="1em" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
          )}

          {/* Operational Cost Categories Section */}
          {activeSection === 'operational-cost-categories' && (
          <div style={{ background: 'white', padding: '32px', borderRadius: '10px', minHeight: 'calc(100vh - 200px)', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <div style={{ marginBottom: '32px', paddingBottom: '20px', borderBottom: '2px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#333' }}>Operational Cost Categories</h2>
            <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#666' }}>Manage categories for operational expenses</p>
          </div>
          {isAdmin && (
            <button className="btn btn-primary" onClick={openAddCategoryModal}>
              <Plus size="1em" style={{marginRight: 6}}/>Add Category
            </button>
          )}
        </div>
        {expenseCategories.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: '#718096',
            fontSize: '14px',
            fontStyle: 'italic'
          }}>
            No operational cost categories found. Create your first category to get started.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Category Name</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Usage</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Description</th>
                {isAdmin && (
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '14px', width: '120px' }}>Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {expenseCategories.map(category => (
                <tr key={category._id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '16px', color: '#111827', fontWeight: '500' }}>{category.name}</td>
                  <td style={{ padding: '16px', color: '#111827' }}>
                    {typeof category.usageCount === 'number' && (
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '500',
                        background: category.usageCount > 0 ? '#fed7d7' : '#c6f6d5',
                        color: category.usageCount > 0 ? '#c53030' : '#22543d'
                      }}>
                        {category.usageCount > 0 ? `${category.usageCount} in use` : 'Not in use'}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '16px', color: '#111827' }}>{category.description || '-'}</td>
                  {isAdmin && (
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button 
                          className="btn btn-secondary" 
                          style={{ padding: '6px 8px', fontSize: '12px' }}
                          onClick={() => openEditCategoryModal(category)}
                          title="Edit Category"
                        >
                          <Edit size="1em" />
                        </button>
                        <button 
                          className="btn btn-danger" 
                          style={{ 
                            padding: '6px 8px', 
                            fontSize: '12px',
                            opacity: typeof category.usageCount === 'number' && category.usageCount > 0 ? 0.5 : 1
                          }}
                          onClick={() => handleCategoryDelete(category)}
                          disabled={typeof category.usageCount === 'number' && category.usageCount > 0}
                          title={typeof category.usageCount === 'number' && category.usageCount > 0 ? 'Cannot delete - category in use' : 'Delete Category'}
                        >
                          <Trash size="1em" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
          )}

      {/* Facility Modal */}
      {facilityModalOpen && (
        <div className="modal-right-overlay">
          <div className="modal-right-content" onClick={e => e.stopPropagation()}>
            <div className="modal-right-header">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#333' }}>
                  {facilityEditId ? 'Edit Facility' : 'Add Facility'}
                </h2>
                <button
                  className="btn"
                  style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#888', cursor: 'pointer', padding: 0 }}
                  onClick={() => setFacilityModalOpen(false)}
                  aria-label="Close"
                >
                  <X />
                </button>
              </div>
            </div>
            <div className="modal-right-body">
              <form onSubmit={handleFacilitySave}>
                <div className="form-group">
                  <label>Facility Name</label>
                  <input
                    type="text"
                    value={facilityInput}
                    onChange={(e) => setFacilityInput(e.target.value)}
                    required
                  />
                </div>
              </form>
            </div>
            <div className="modal-right-footer">
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setFacilityModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={facilityLoading} onClick={handleFacilitySave}>
                  {facilityLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Category Modal */}
      {categoryModalOpen && (
        <div className="modal-right-overlay">
          <div className="modal-right-content" onClick={e => e.stopPropagation()}>
            <div className="modal-right-header">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#333' }}>
                  {categoryEditId ? 'Edit Category' : 'Add Category'}
                </h2>
                <button
                  className="btn"
                  style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#888', cursor: 'pointer', padding: 0 }}
                  onClick={() => setCategoryModalOpen(false)}
                  aria-label="Close"
                >
                  <X />
                </button>
              </div>
            </div>
            <div className="modal-right-body">
              <form onSubmit={handleCategorySave}>
                <div className="form-group">
                  <label>Category Name</label>
                  <input
                    type="text"
                    value={categoryInput}
                    onChange={(e) => setCategoryInput(e.target.value)}
                    required
                  />
                </div>
              </form>
            </div>
            <div className="modal-right-footer">
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setCategoryModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={categoryLoading} onClick={handleCategorySave}>
                  {categoryLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Category Modal */}
      {invoiceCategoryModalOpen && (
        <div className="modal-right-overlay">
          <div className="modal-right-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-right-header">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#333' }}>
                  {invoiceCategoryEditId ? 'Edit Invoice Category' : 'Add Invoice Category'}
                </h2>
                <button
                  className="btn"
                  style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#888', cursor: 'pointer', padding: 0 }}
                  onClick={() => setInvoiceCategoryModalOpen(false)}
                  aria-label="Close"
                >
                  <X />
                </button>
              </div>
            </div>
            <div className="modal-right-body">
              <form onSubmit={handleInvoiceCategorySave}>
                <div className="form-group">
                  <label>Invoice Category Name</label>
                  <input
                    type="text"
                    value={invoiceCategoryInput}
                    onChange={(e) => setInvoiceCategoryInput(e.target.value)}
                    required
                  />
                </div>
              </form>
            </div>
            <div className="modal-right-footer">
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setInvoiceCategoryModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={invoiceCategoryLoading}
                  onClick={handleInvoiceCategorySave}
                >
                  {invoiceCategoryLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Department Modal */}
      {departmentModalOpen && (
        <div className="modal-right-overlay">
          <div className="modal-right-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-right-header">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#333' }}>
                  {departmentEditId ? 'Edit Department' : 'Add Department'}
                </h2>
                <button
                  className="btn"
                  style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#888', cursor: 'pointer', padding: 0 }}
                  onClick={() => setDepartmentModalOpen(false)}
                  aria-label="Close"
                >
                  <X />
                </button>
              </div>
            </div>
            <div className="modal-right-body">
              <form onSubmit={handleDepartmentSave}>
                <div className="form-group">
                  <label>Department Name</label>
                  <input
                    type="text"
                    value={departmentInput}
                    onChange={(e) => setDepartmentInput(e.target.value)}
                    placeholder="e.g. Operations, IT, Finance"
                    required
                  />
                </div>
              </form>
            </div>
            <div className="modal-right-footer">
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setDepartmentModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={departmentLoading}
                  onClick={handleDepartmentSave}
                >
                  {departmentLoading ? 'Saving...' : departmentEditId ? 'Update' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Rule Modal */}
      {invoiceRuleModalOpen && (
        <div className="modal-right-overlay">
          <div className="modal-right-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-right-header">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#333' }}>
                  {invoiceRuleEditId ? 'Edit Invoice Rule' : 'Add Invoice Rule'}
                </h2>
                <button
                  className="btn"
                  style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#888', cursor: 'pointer', padding: 0 }}
                  onClick={() => {
                    setInvoiceRuleModalOpen(false);
                    setSelectedFacilityId('');
                    setSelectedInvoiceCategoryId('');
                    setSelectedInvoiceTypeId('');
                    setSelectedBankAccountId('');
                    setInvoiceRuleEditId(null);
                  }}
                  aria-label="Close"
                >
                  <X />
                </button>
              </div>
            </div>
            <div className="modal-right-body">
              <form onSubmit={handleInvoiceRuleSave}>
                <div className="form-group">
                  <label>Select Facility *</label>
                  <select
                    value={selectedFacilityId}
                    onChange={(e) => {
                      setSelectedFacilityId(e.target.value);
                      // Reset dependent dropdowns when facility changes
                      setSelectedInvoiceCategoryId('');
                      setSelectedInvoiceTypeId('');
                      setSelectedBankAccountId('');
                    }}
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #ddd',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">-- Select Facility --</option>
                    {facilities.map((facility) => {
                      const facilityName = facility.name || facility.facilityName || `Facility ${facility.id}`;
                      return (
                        <option key={facility.id} value={facility.id}>
                          {facilityName}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="form-group">
                  <label>Select Invoice Category *</label>
                  <select
                    value={selectedInvoiceCategoryId}
                    onChange={(e) => {
                      setSelectedInvoiceCategoryId(e.target.value);
                      // Reset dependent dropdowns when category changes
                      setSelectedInvoiceTypeId('');
                      setSelectedBankAccountId('');
                    }}
                    required
                    disabled={!selectedFacilityId}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #ddd',
                      fontSize: '14px',
                      backgroundColor: !selectedFacilityId ? '#f5f5f5' : 'white',
                      cursor: !selectedFacilityId ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <option value="">-- Select Invoice Category --</option>
                    {invoiceCategories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  {!selectedFacilityId && (
                    <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                      Please select a facility first
                    </small>
                  )}
                </div>
                <div className="form-group">
                  <label>Select Invoice Type *</label>
                  <select
                    value={selectedInvoiceTypeId}
                    onChange={(e) => {
                      setSelectedInvoiceTypeId(e.target.value);
                      // Reset bank account when invoice type changes
                      setSelectedBankAccountId('');
                    }}
                    required
                    disabled={!selectedInvoiceCategoryId}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #ddd',
                      fontSize: '14px',
                      backgroundColor: !selectedInvoiceCategoryId ? '#f5f5f5' : 'white',
                      cursor: !selectedInvoiceCategoryId ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <option value="">-- Select Invoice Type --</option>
                    {invoiceTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                  {!selectedInvoiceCategoryId && (
                    <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                      Please select an invoice category first
                    </small>
                  )}
                </div>
                <div className="form-group">
                  <label>Select Bank Account *</label>
                  <select
                    value={selectedBankAccountId}
                    onChange={(e) => setSelectedBankAccountId(e.target.value)}
                    required
                    disabled={!selectedInvoiceTypeId}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #ddd',
                      fontSize: '14px',
                      backgroundColor: !selectedInvoiceTypeId ? '#f5f5f5' : 'white',
                      cursor: !selectedInvoiceTypeId ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <option value="">-- Select Bank Account --</option>
                    {config.bankAccounts.map((bank) => {
                      // Use custom title if available, otherwise fall back to "Bank Name - Account Name"
                      const displayName = (bank.customTitle && bank.customTitle.trim() !== '') 
                        ? bank.customTitle 
                        : `${bank.bankName} - ${bank.accountName}`;
                      return (
                        <option key={bank.id} value={bank.id}>
                          {displayName}
                        </option>
                      );
                    })}
                  </select>
                  {!selectedInvoiceTypeId && (
                    <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                      Please select an invoice type first
                    </small>
                  )}
                </div>
              </form>
            </div>
            <div className="modal-right-footer">
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setInvoiceRuleModalOpen(false);
                    setSelectedFacilityId('');
                    setSelectedInvoiceCategoryId('');
                    setSelectedInvoiceTypeId('');
                    setSelectedBankAccountId('');
                    setInvoiceRuleEditId(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={invoiceRuleLoading || !selectedFacilityId || !selectedInvoiceCategoryId || !selectedInvoiceTypeId || !selectedBankAccountId}
                  onClick={handleInvoiceRuleSave}
                >
                  {invoiceRuleLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Type Modal */}
      {invoiceTypeModalOpen && (
        <div className="modal-right-overlay">
          <div className="modal-right-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-right-header">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#333' }}>
                  {invoiceTypeEditId ? 'Edit Invoice Type' : 'Add Invoice Type'}
                </h2>
                <button
                  className="btn"
                  style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#888', cursor: 'pointer', padding: 0 }}
                  onClick={() => {
                    setInvoiceTypeModalOpen(false);
                    setInvoiceTypeInput('');
                    setInvoiceTypeEditId(null);
                  }}
                  aria-label="Close"
                >
                  <X />
                </button>
              </div>
            </div>
            <div className="modal-right-body">
              <form onSubmit={handleInvoiceTypeSave}>
                <div className="form-group">
                  <label>Invoice Type</label>
                  <input
                    type="text"
                    value={invoiceTypeInput}
                    onChange={(e) => setInvoiceTypeInput(e.target.value)}
                    placeholder="Enter invoice type name"
                    required
                  />
                </div>
              </form>
            </div>
            <div className="modal-right-footer">
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setInvoiceTypeModalOpen(false);
                    setInvoiceTypeInput('');
                    setInvoiceTypeEditId(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={invoiceTypeLoading || !invoiceTypeInput.trim()}
                  onClick={handleInvoiceTypeSave}
                >
                  {invoiceTypeLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {renderEditModal()}
      {renderBankModal()}

      {/* Signature Preview Modal */}
      {signaturePreviewOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => setSignaturePreviewOpen(false)}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            position: 'relative',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }} onClick={e => e.stopPropagation()}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Signature Preview</h3>
              <button
                onClick={() => setSignaturePreviewOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '4px',
                  borderRadius: '4px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                ×
              </button>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px',
              border: '2px solid #e1e5ee',
              borderRadius: '8px',
              background: '#fafafa',
              padding: '20px'
            }}>
              <img 
                src={config.termSheetSignatureUrl} 
                alt="Signature Full Size" 
                style={{ 
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  display: 'block'
                }}
                onError={(e) => {
                  console.error('Failed to load signature image in modal:', config.termSheetSignatureUrl);
                  e.target.style.display = 'none';
                }}
              />
            </div>
            <div style={{
              marginTop: '12px',
              fontSize: '12px',
              color: '#666',
              textAlign: 'center'
            }}>
              This signature will be used in all Term Sheet PDFs
            </div>
          </div>
        </div>
      )}
        </div>
      </div>
    </div>
  );
}

export default Configurations; 